import { useTranslation } from 'react-i18next';

import { Button } from '~/chords/Button';
import { Icon } from '~/chords/Icon';
import { Tooltip } from '~/components';
import { cn } from '~/lib/cn';
import { env } from '~/lib/env';

export function GlobalHeaderButtons() {
  const { t } = useTranslation('wmgone', {
    keyPrefix: 'globalHeader',
  });

  const feedbackUrl = env('VITE_WMG_FEEDBACK_URL');
  const productGuideUrl = env('VITE_WMG_PRODUCT_GUIDE_URL');
  const supportHubUrl = env('VITE_WMG_SUPPORT_HUB_URL');

  return (
    <div className="flex items-stretch gap-md justify-self-end">
      {feedbackUrl ? (
        <Tooltip
          size="deprecated-help"
          spacing="thin"
          content={
            <>
              <div className="mb-sm leading-tight">
                <div className="mb-md basis-full text-body-md font-semibold">
                  {t('feedback.tooltip.heading')}
                </div>
                <div className="flex gap-md">
                  <img
                    src="/images/feedback-avatar-geo.jpg"
                    alt={t('feedback.tooltip.avatar')}
                    className="aspect-square size-xl basis-xl rounded-full object-cover outline outline-2 transition-transform duration-300 ease-out bg-emphasis-subtle outline-emphasis-subtle hover:scale-150"
                  />
                  <p className="text-body-sm">
                    {t('feedback.tooltip.description')}
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="primary" asChild>
                  <a
                    href={feedbackUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {t('feedback.tooltip.link')}
                  </a>
                </Button>
              </div>
            </>
          }
          triggerAsChild
          sideOffset={8}
          alignOffset={-8}
          delayDuration={0}
          align="end"
        >
          <div className="flex items-stretch">
            <Button variant="secondary" asChild>
              <a
                href={feedbackUrl}
                className="grid place-items-center"
                target="_blank"
                rel="noreferrer noopener"
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
              >
                {t('feedback.button')}
              </a>
            </Button>
          </div>
        </Tooltip>
      ) : null}
      {productGuideUrl || supportHubUrl ? (
        <Tooltip
          size="deprecated-help"
          spacing="thin"
          content={
            <>
              {productGuideUrl ? (
                <div
                  className={cn({
                    'mb-md border-b border-solid pb-md border-emphasis-subtle':
                      supportHubUrl,
                  })}
                >
                  <p className="mb-sm text-caption-md leading-tight">
                    {t('help.tooltip.productGuide.description')}
                  </p>
                  <a
                    href={productGuideUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group mb-2xs inline-flex items-center gap-xs"
                  >
                    <div className="font-semibold underline underline-offset-4 group-hover:no-underline">
                      {t('help.tooltip.productGuide.link')}
                    </div>
                    <div className="pt-2xs">
                      <Icon type="arrow-up-right-from-square" size="sm" />
                    </div>
                  </a>
                </div>
              ) : null}
              {supportHubUrl ? (
                <div>
                  <p className="mb-sm text-caption-md">
                    {t('help.tooltip.supportHub.description')}
                  </p>
                  <a
                    href={supportHubUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group mb-2xs inline-flex items-center gap-xs"
                  >
                    <div className="font-semibold underline underline-offset-4 group-hover:no-underline">
                      {t('help.tooltip.supportHub.link')}
                    </div>
                    <div className="pt-2xs">
                      <Icon type="arrow-up-right-from-square" size="sm" />
                    </div>
                  </a>
                </div>
              ) : null}
            </>
          }
          triggerAsChild
          sideOffset={8}
          alignOffset={-8}
          delayDuration={0}
          align="end"
          closeOnClick={false}
        >
          <div className="flex items-stretch">
            <Button variant="secondary" asChild>
              <button
                className="group grid aspect-square scale-110 grid-cols-1 grid-rows-1 place-items-center ease-out"
                aria-label={t('help.button')}
              >
                <div className="col-start-1 row-start-1 leading-[0] opacity-none duration-300 group-hover:opacity-full">
                  <Icon type="circle-question" variant="solid" />
                </div>
                <div className="col-start-1 row-start-1 leading-[0] opacity-full duration-300 group-hover:opacity-none">
                  <Icon type="circle-question" variant="regular" />
                </div>
              </button>
            </Button>
          </div>
        </Tooltip>
      ) : null}
    </div>
  );
}

GlobalHeaderButtons.displayName = 'GlobalHeaderButtons';
